import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Calendar, ExternalLink, Loader2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import MarkdownContent from '../components/MarkdownContent';
import ShareBar from '../components/ShareBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://zebraonegallery.com';

const NewsArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        // Public route — works with the existing `id`-as-slug pattern.
        const res = await fetch(`${API_URL}/api/news/${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error(`Not found (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        setArticle(data);

        // Fetch the full news list once to compute "related" — same year first,
        // then same publication, then same category, dedup against the current.
        const allRes = await fetch(`${API_URL}/api/news?limit=200`);
        const all = allRes.ok ? await allRes.json() : [];
        if (cancelled) return;
        const candidates = all.filter((n) => n.id !== data.id);
        const score = (n) => {
          let s = 0;
          if (n.year === data.year) s += 3;
          if (n.publication && n.publication === data.publication) s += 2;
          if (n.category && n.category === data.category) s += 1;
          return s;
        };
        const ranked = candidates
          .map((n) => [n, score(n)])
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4)
          .map(([n]) => n);
        setRelated(ranked);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load article');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchArticle();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return () => { cancelled = true; };
  }, [slug]);

  // ── Loading / error states ──
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-3" />
          Loading article…
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-24 text-center" data-testid="news-article-error">
          <h1 className="font-serif text-3xl mb-3">Article not found</h1>
          <p className="text-muted-foreground mb-6">
            The article you&apos;re looking for may have been removed or had its URL changed.
          </p>
          <button
            type="button"
            onClick={() => navigate('/news')}
            className="inline-flex items-center gap-2 px-6 py-3 border border-foreground hover:bg-foreground hover:text-background transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to News
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Main render ──
  const heroImage = article.image_url;
  // SEO description: first 160 chars of stripped-Markdown content
  const seoDescription = (article.content || '')
    .replace(/[#*_>[\]()`]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);

  return (
    <div className="min-h-screen bg-background" data-testid="news-article-page">
      <SEO
        title={`${article.title} | Zebra One Gallery`}
        description={seoDescription}
        image={heroImage}
        canonical={`${SITE_URL}/news/${article.id}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: article.title,
          datePublished: article.iso_date || article.date,
          author: { '@type': 'Organization', name: 'Zebra One Gallery' },
          publisher: {
            '@type': 'Organization',
            name: 'Zebra One Gallery',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
          },
          image: heroImage,
          articleSection: article.category,
          mainEntityOfPage: `${SITE_URL}/news/${article.id}`,
        }}
      />
      <Header />

      {/* Breadcrumbs */}
      <nav
        className="container mx-auto px-6 pt-8 text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"
        aria-label="Breadcrumb"
        data-testid="news-article-breadcrumb"
      >
        <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/news" className="hover:text-foreground transition-colors">News</Link>
        {article.year && (
          <>
            <span aria-hidden="true">/</span>
            <Link to={`/news?year=${article.year}`} className="hover:text-foreground transition-colors">
              {article.year}
            </Link>
          </>
        )}
        <span aria-hidden="true">/</span>
        <span className="text-foreground line-clamp-1">{article.title}</span>
      </nav>

      {/* Hero */}
      <article className="container mx-auto px-6 pt-10 pb-20 max-w-4xl">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          {article.category && (
            <span
              className="inline-block px-3 py-1 mb-6 text-xs uppercase tracking-[0.2em] bg-foreground text-background"
              data-testid="news-article-category"
            >
              {article.category}
            </span>
          )}
          <h1
            className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6 text-foreground"
            data-testid="news-article-title"
          >
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground font-sans">
            {article.date && (
              <span className="inline-flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {article.date}
              </span>
            )}
            {article.publication && (
              <span className="uppercase tracking-[0.15em] text-xs">
                {article.publication}
              </span>
            )}
            {article.url && (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] underline underline-offset-4"
              >
                <ExternalLink className="w-3 h-3" /> Original source
              </a>
            )}
          </div>
        </motion.header>

        {heroImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-12 -mx-6 md:mx-0"
          >
            <img
              src={heroImage}
              alt={article.title}
              className="w-full h-auto"
              data-testid="news-article-hero"
            />
          </motion.div>
        )}

        {/* Body */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="font-sans text-lg text-foreground leading-relaxed"
          data-testid="news-article-body"
        >
          <MarkdownContent>{article.content}</MarkdownContent>
        </motion.div>

        {/* Share bar */}
        {article && (
          <div
            className="mt-16 pt-8 border-t border-border"
            data-testid="news-article-share"
          >
            <ShareBar
              url={`${SITE_URL}/news/${article.id}`}
              title={article.title}
              image={article.image_url}
            />
          </div>
        )}
      </article>

      {/* Related posts rail */}
      {related.length > 0 && (
        <section
          className="border-t border-border bg-muted/20 py-16"
          aria-labelledby="related-posts-title"
          data-testid="news-related-rail"
        >
          <div className="container mx-auto px-6 max-w-6xl">
            <h2
              id="related-posts-title"
              className="font-serif text-2xl md:text-3xl mb-8 text-foreground"
            >
              Related coverage
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/news/${r.id}`}
                  className="group block bg-background border border-border hover:shadow-lg transition-shadow"
                  data-testid={`related-post-${r.id}`}
                >
                  {r.image_url ? (
                    <div className="aspect-[4/3] overflow-hidden bg-muted">
                      <img
                        src={r.image_url}
                        alt={r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-muted flex items-center justify-center font-serif text-3xl text-muted-foreground">
                      {r.year || '—'}
                    </div>
                  )}
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                      {r.date || r.year}
                      {r.publication && ` · ${r.publication}`}
                    </p>
                    <h3 className="font-serif text-lg leading-snug text-foreground group-hover:text-muted-foreground transition-colors line-clamp-3">
                      {r.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-6 py-3 border border-foreground hover:bg-foreground hover:text-background transition-colors text-sm uppercase tracking-[0.2em]"
              >
                <ArrowLeft className="w-4 h-4" />
                All news &amp; press
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default NewsArticlePage;
